import React, { useState } from 'react';
import { TI_GOAL_TYPES, TI_STATUSES } from '../../triage/config';
import { getTriageCurrentUserId } from '../../triage/identity';
import { tiStaffById } from '../../triage/utils';
import type { TriageItem, TriageStatus } from '../../triage/types';
import { Icon } from '../Icon/Icon';
import { TriageAssigneeAvatar } from './TriageAssigneeAvatar';
import { TriageAssigneeMenu } from './TriageAssigneeMenu';
import { TriageNotesCard } from './TriageNotesCard';
import { TriagePatientInfoStrip } from './TriagePatientInfoStrip';
import { TriageSourceLink } from './TriageSourceLink';
import { TriageStatusMenu } from './TriageStatusMenu';
import { TriageTriggerBanner } from './TriageTriggerBanner';

function priorChannelIcon(channel: string) {
  if (channel === 'SMS') return 'chat' as const;
  if (channel === 'Email') return 'mail' as const;
  return 'phone' as const;
}

export interface TriageCallTaskDetailProps {
  item: TriageItem;
  noteText: string;
  setNoteText: (value: string) => void;
  onChangeStatus: (id: number, status: TriageStatus) => void;
  onChangeAssignee: (id: number, assigneeId: string | null) => void;
  onAddNote: (id: number) => void;
  onOpenSource?: (item: TriageItem) => void;
}

export function TriageCallTaskDetail({
  item,
  noteText,
  setNoteText,
  onChangeStatus,
  onChangeAssignee,
  onAddNote,
  onOpenSource,
}: TriageCallTaskDetailProps) {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
  const assignee = tiStaffById(item.assigneeId);
  const goal = TI_GOAL_TYPES[item.goalType ?? 'book'] ?? TI_GOAL_TYPES.book!;
  const ct = item.callTask ?? {};
  const isClosed = item.status === 'closed';
  const statusCfg = TI_STATUSES[item.status];

  return (
    <div className="cal-scroll flex min-h-0 flex-1 overflow-y-auto" style={{ background: 'var(--cream-0)' }}>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '24px 32px 60px', width: '100%' }}>
        <div
          className="v6-fade-up v6-d-0"
          style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8, flexWrap: 'wrap' }}>
              <span
                className="ti-channel-chip"
                style={{
                  background: 'var(--success-tint)',
                  color: 'color-mix(in srgb, var(--success) 80%, #000)',
                  fontSize: 11,
                  padding: '2px 9px',
                }}
              >
                <Icon kind="phone" size={11} stroke={2.2} /> Call to make
              </span>
              <h1
                className="v6-display"
                style={{
                  fontSize: 24,
                  fontWeight: 600,
                  letterSpacing: '-0.025em',
                  color: 'var(--ink-0)',
                  margin: 0,
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.patient}
              </h1>
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', letterSpacing: '-0.005em', marginBottom: 12 }}>
              {item.subject} <span style={{ color: 'var(--ink-3)' }}>· {item.time}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() =>
                    (statusCfg?.canTransitionTo.length ?? 0) > 0 && setStatusMenuOpen((o) => !o)
                  }
                  style={{
                    background: statusCfg?.bg,
                    color: statusCfg?.color,
                    border: `1px solid ${statusCfg?.bg}`,
                    borderRadius: 999,
                    padding: '4px 12px 4px 10px',
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: (statusCfg?.canTransitionTo.length ?? 0) > 0 ? 'pointer' : 'default',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  <span className="ti-status-dot" style={{ background: statusCfg?.dot, width: 8, height: 8 }} />
                  {statusCfg?.label}
                  {(statusCfg?.canTransitionTo.length ?? 0) > 0 ? (
                    <Icon kind="down" size={10} stroke={2.2} />
                  ) : null}
                </button>
                <TriageStatusMenu
                  open={statusMenuOpen}
                  currentStatus={item.status}
                  onSelect={(next) => {
                    onChangeStatus(item.id, next);
                    setStatusMenuOpen(false);
                  }}
                  onClose={() => setStatusMenuOpen(false)}
                  anchorStyle={{ left: 0, top: 'calc(100% + 4px)' }}
                />
              </div>

              <div style={{ position: 'relative' }}>
                {assignee ? (
                  <button
                    type="button"
                    onClick={() => setAssigneeMenuOpen((o) => !o)}
                    className="ti-chip-btn ti-chip-btn-assigned"
                    style={{ padding: '3px 11px 3px 4px', fontSize: 12 }}
                  >
                    <TriageAssigneeAvatar staff={assignee} size={22} />
                    <span style={{ fontWeight: 600 }}>{assignee.name}</span>
                    <Icon kind="down" size={10} stroke={2} color="var(--ink-3)" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAssigneeMenuOpen((o) => !o)}
                    className="ti-chip-btn ti-chip-btn-empty"
                    style={{ padding: '4px 12px', fontSize: 12 }}
                  >
                    <Icon kind="plus" size={12} stroke={2} />
                    <span>Assign</span>
                    <Icon kind="down" size={10} stroke={2} />
                  </button>
                )}
                <TriageAssigneeMenu
                  open={assigneeMenuOpen}
                  currentAssigneeId={item.assigneeId}
                  onSelect={(id) => {
                    onChangeAssignee(item.id, id);
                    setAssigneeMenuOpen(false);
                  }}
                  onClose={() => setAssigneeMenuOpen(false)}
                  anchorStyle={{ left: 0, top: 'calc(100% + 4px)' }}
                />
              </div>

              {!assignee && getTriageCurrentUserId() ? (
                <button
                  type="button"
                  onClick={() => onChangeAssignee(item.id, getTriageCurrentUserId())}
                  style={{
                    padding: '4px 11px',
                    borderRadius: 999,
                    background: 'color-mix(in srgb, var(--success) 7%, transparent)',
                    border: '1px solid var(--success-tint)',
                    color: 'color-mix(in srgb, var(--success) 80%, #000)',
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  Assign to me
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <TriageTriggerBanner
          triggeredBy={item.triggeredBy ?? 'recall'}
          sourceLabel={ct.recallName || item.sourceLabel}
          surfacesIn={item.surfacesIn}
        />

        <div style={{ marginBottom: 14 }}>
          <TriageSourceLink item={item} {...(onOpenSource ? { onOpen: onOpenSource } : {})} />
        </div>

        {item.patientInfo ? <TriagePatientInfoStrip info={item.patientInfo} /> : null}

        {ct.contextCard && ct.contextCard.length > 0 ? (
          <section className="v6-card v6-fade-up v6-d-2" style={{ marginBottom: 14 }}>
            <header
              style={{
                padding: '14px 20px 12px',
                borderBottom: '1px solid var(--cream-2)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Icon kind="team" size={14} stroke={2} color="color-mix(in srgb, var(--gold) 78%, #000)" />
              <span className="v6-display" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.01em' }}>
                Before you call
              </span>
            </header>
            <div
              style={{
                padding: '14px 20px 16px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px 24px',
              }}
            >
              {ct.contextCard.map((f, i) => (
                <div key={`${f.label}-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="v6-eyebrow" style={{ fontSize: 9.5 }}>
                    {f.label}
                  </span>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.005em' }}>
                    {f.value}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {ct.script ? (
          <section className="ti-ai-card v6-fade-up v6-d-3" style={{ marginBottom: 14 }}>
            <header
              style={{
                padding: '14px 20px 12px',
                borderBottom: '1px solid var(--success-tint)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Icon kind="sparkle" size={14} stroke={2} color="color-mix(in srgb, var(--success) 80%, #000)" />
              <span
                className="v6-display"
                style={{ fontSize: 14, fontWeight: 600, color: 'color-mix(in srgb, var(--success) 80%, #000)', letterSpacing: '-0.01em' }}
              >
                Suggested call script
              </span>
              <span className="v6-mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 4 }}>
                {goal.label}
              </span>
            </header>
            <div style={{ padding: '16px 20px 18px' }}>
              <p style={{ fontSize: 13.5, color: 'var(--ink-0)', margin: 0, letterSpacing: '-0.005em', lineHeight: 1.7 }}>
                {ct.script}
              </p>
            </div>
          </section>
        ) : null}

        {ct.priorAttempts && ct.priorAttempts.length > 0 ? (
          <section className="v6-card v6-fade-up v6-d-3" style={{ marginBottom: 14 }}>
            <header
              style={{
                padding: '14px 20px 12px',
                borderBottom: '1px solid var(--cream-2)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Icon kind="activity" size={14} stroke={2} color="var(--ink-2)" />
              <span className="v6-display" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-0)', letterSpacing: '-0.01em' }}>
                What Elva already tried
              </span>
            </header>
            <div style={{ padding: '12px 20px 16px' }}>
              {ct.priorAttempts.map((a, i) => (
                <div
                  key={`${a.label}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 11,
                    padding: '8px 0',
                    borderBottom: i < ct.priorAttempts!.length - 1 ? '1px solid var(--cream-2)' : 'none',
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                      background: 'var(--surface-mute)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--ink-2)',
                      flex: 'none',
                    }}
                  >
                    <Icon kind={priorChannelIcon(a.channel)} size={13} stroke={2} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-0)', letterSpacing: '-0.005em' }}>
                      {a.label}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-2)', letterSpacing: '-0.005em' }}>{a.result}</div>
                  </div>
                  <span className="v6-mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', flex: 'none' }}>
                    {a.time}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <TriageNotesCard
          item={item}
          noteText={noteText}
          setNoteText={setNoteText}
          onAdd={() => onAddNote(item.id)}
          cannedSnippets={!isClosed ? goal.cannedNotes : null}
        />
      </div>
    </div>
  );
}
